import React, { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { ShoppingBag, FileText, Receipt, AlertTriangle, Award } from "lucide-react";

export default function DistributorDashboard() {
    const { profile } = useOutletContext() || {};
    const [stmt, setStmt] = useState({ orders: [], invoices: [], outstanding: 0, total_billed: 0, total_paid: 0 });
    const [tier, setTier] = useState(null);

    useEffect(() => {
        api.get("/distributors/statement").then(r => setStmt(r.data)).catch(() => {});
        api.get("/distributors/me/tier").then(r => setTier(r.data)).catch(() => {});
    }, []);

    const hasTier = tier && (tier.tier_label || tier.discount_pct > 0 || tier.cash_discount_pct > 0 || tier.bonus_notes);

    return (
        <div data-testid="dist-dashboard" className="space-y-6">
            <div>
                <div className="label-eyebrow text-sky-700">Welcome back</div>
                <h1 className="font-serif text-4xl text-stone-900 mt-2">{profile?.business_name || "Distributor"}</h1>
                <p className="text-sm text-stone-500 mt-1">{profile?.contact_name} · {profile?.city}, {profile?.state}</p>
            </div>

            {profile?.status === "pending" && (
                <div className="border border-amber-300 bg-amber-50 p-5 flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium text-amber-900">Awaiting admin approval</div>
                        <p className="text-sm text-amber-800 mt-1">You'll be able to view distributor pricing and place orders once your account is approved.</p>
                    </div>
                </div>
            )}

            <div className="grid sm:grid-cols-3 gap-4">
                <Stat label="Total billed" value={formatINR(stmt.total_billed)} icon={FileText} />
                <Stat label="Total paid" value={formatINR(stmt.total_paid)} icon={Receipt} />
                <Stat label="Outstanding" value={formatINR(stmt.outstanding)} icon={AlertTriangle} highlight={stmt.outstanding > 0} />
            </div>

            {hasTier && (
                <div className="border-l-4 border-sky-700 bg-white p-5" data-testid="dist-tier-card">
                    <div className="flex items-start gap-3">
                        <Award className="w-5 h-5 text-sky-700 mt-1 shrink-0" />
                        <div className="flex-1">
                            <div className="label-eyebrow text-sky-700">Your wholesale tier</div>
                            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-2">
                                <span className="font-serif text-2xl text-stone-900">{tier.tier_label || "Default"}</span>
                                <span className="text-xs text-stone-500">Mode: {tier.mode}</span>
                                {tier.discount_pct > 0 && <span className="px-2 py-0.5 bg-sky-100 text-sky-800 text-xs font-semibold">{tier.discount_pct}% off</span>}
                                {tier.cash_discount_pct > 0 && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-semibold">+{tier.cash_discount_pct}% cash</span>}
                            </div>
                            {tier.ytd_business > 0 && (
                                <p className="text-xs text-stone-500 mt-2">FY business so far: {formatINR(tier.ytd_business)}</p>
                            )}
                            {tier.bonus_notes && (
                                <div className="mt-3 bg-amber-50 border border-amber-200 p-3">
                                    <div className="label-eyebrow text-amber-700">Bonus structure</div>
                                    <pre className="whitespace-pre-wrap font-sans text-sm text-stone-800 mt-1">{tier.bonus_notes}</pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-4">
                <Link to="/distributor/shop" className="bg-sky-700 text-stone-50 p-6 hover:bg-sky-800 transition-colors flex items-start gap-4 border border-sky-700" data-testid="dist-quick-shop">
                    <ShoppingBag className="w-6 h-6 mt-1" />
                    <div>
                        <div className="font-serif text-2xl">Place a new order</div>
                        <p className="text-sm opacity-80 mt-1">Browse the full catalog with your distributor pricing.</p>
                    </div>
                </Link>
                <Link to="/distributor/invoices" className="bg-white border border-stone-200 p-6 hover:border-stone-400 transition-colors flex items-start gap-4" data-testid="dist-quick-invoices">
                    <FileText className="w-6 h-6 mt-1 text-sky-700" />
                    <div>
                        <div className="font-serif text-2xl text-stone-900">Download invoices</div>
                        <p className="text-sm text-stone-500 mt-1">Every invoice issued by accounts, ready to print or save as PDF.</p>
                    </div>
                </Link>
                <Link to="/distributor/statement" className="bg-white border border-stone-200 p-6 hover:border-stone-400 transition-colors flex items-start gap-4">
                    <Receipt className="w-6 h-6 mt-1 text-sky-700" />
                    <div>
                        <div className="font-serif text-2xl text-stone-900">View statement</div>
                        <p className="text-sm text-stone-500 mt-1">Filterable ledger of orders, invoices and payments.</p>
                    </div>
                </Link>
            </div>
        </div>
    );
}

const Stat = ({ label, value, icon: Icon, highlight }) => (
    <div className={`bg-white border ${highlight ? "border-amber-500" : "border-stone-200"} p-5`}>
        <div className="flex items-center justify-between"><div className="label-eyebrow text-stone-500">{label}</div><Icon className={`w-4 h-4 ${highlight ? "text-amber-600" : "text-sky-700"}`} /></div>
        <div className={`font-serif text-3xl mt-2 ${highlight ? "text-amber-700" : "text-stone-900"}`}>{value}</div>
    </div>
);
